//  ----------      START   IMPORT  BLOCK       ----------
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
//  Widgets
import { Badge, Navbar, Nav, NavDropdown, Button, OverlayTrigger, Tooltip } from 'react-bootstrap';
import Form from 'react-bootstrap/Form';
import InputGroup from 'react-bootstrap/InputGroup';
import QRModal from '../Widgets/General/QRModal/QRModal';
//  Context & Hooks
import { useLanguage } from '../../API/Context/LanguageContext';
import { usePatient } from '../../API/Hooks/PatientContext';
import { useActivePatient } from '../../API/Hooks/ActivePatientContext';
import { useUser } from '../../API/Context/UserContext';
import { useApplicationUI } from '../../API/Context/AppUIContext';
//  Icons
import { BsBell, BsGear } from 'react-icons/bs';
import logo from '../../Assets/Images/cleir_logo.png';
import clean_icon from '../../Assets/Icons/Generic/clean_icon.png';
//  Config
import { FRONTEND_ROUTES } from '../../ConfigFiles/Routes/frontend_routes';
import VocabGreetings from '../../ConfigFiles/multilingual/VocabGreetings';
import VocabTopBar from '../../ConfigFiles/multilingual/VocabTopBar';
import './TopBar.css';
//  ----------      END   IMPORT  BLOCK       ----------

const TopBar = ({ onToggleSidebar }) => {
  //  Consume contexts
  const { user, isLoading, isProfessional } = useUser();
  const { lan } = useLanguage();
  const { isProfessionalDashboardActive, toggleProfessionalDashboard } = useApplicationUI();
  
  //  Active Patient Context & Patient Cache Context
  const { activePatient, loadSelectedPatient, resetToSelf } = useActivePatient();
  const { fetchPatientByQuery } = usePatient();

  const navigate = useNavigate();

  const [shake, setShake] = useState(false);
  const [searchUser, setSearchUser] = useState('');
  const [ShowQR, setShowQR] = useState(false);

  const unreadCount = 3;

  // Derive display name for active patient safely
  const activePatientFullName = activePatient
    ? [activePatient.patient?.name, activePatient.patient?.lastname_1].filter(Boolean).join(' ')
    : null;

  const user_fullname = isLoading
    ? 'Cargando...'
    : user
    ? [isProfessional ? 'Dr.' : null, user.name].filter(Boolean).join(' ')
    : 'Invitado';

  const handleNotificationsClick = () => {
    // Open modal or dropdown
  };

  const handleShake = () => {
    setShake(true);
    setTimeout(() => setShake(false), 500);
  };

  function getGreetingMessage() {
    const currentHour = new Date().getHours();
    if (currentHour < 12) return VocabGreetings.good_morning[lan];
    if (currentHour < 18) return VocabGreetings.good_afternoon[lan];
    return VocabGreetings.good_night[lan];
  }

  const handleClear = () => {
  resetToSelf();
  setSearchUser('');
  };

  const handleLogout = () => {
    navigate(FRONTEND_ROUTES.landing);
  };

  const handleProfileChange = () => {
    toggleProfessionalDashboard();
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    const query = searchUser.trim();
    if (!query) return;

    try {
      const result = await loadSelectedPatient(query);

      // Trigger shake if no patient was found or patient object is missing
      if (!result || !result.patient) {
        handleShake();
      }
    } catch (error) {
      handleShake();
      console.error('Error searching patient:', error);
    }
  };

  return (
    <>
      {isProfessional && (
        <QRModal
          show={ShowQR}
          handleClose={() => setShowQR(false)}
          destinationLink={FRONTEND_ROUTES.SERVER + "/" + FRONTEND_ROUTES.invited_static + user?.username}
        />
      )}

      <Navbar bg="dark" variant="dark" expand="lg" sticky="top">
        {/* Mobile Toggle Button */}
        <Button variant="dark" className="d-md-none me-2" onClick={onToggleSidebar}>
          <span className="navbar-toggler-icon"></span>
        </Button>

        <Navbar.Brand onClick={() => navigate(FRONTEND_ROUTES.dashboard)}>
          <OverlayTrigger
            placement="bottom"
            overlay={<Tooltip id="navbar-brand-tooltip">{VocabTopBar.home[lan]}</Tooltip>}
          >
            <img
              src={logo}
              width="40"
              height="40"
              className="d-inline-block align-top logo-img"
              alt="App logo"
            />
          </OverlayTrigger>
        </Navbar.Brand>

        <Navbar.Toggle aria-controls="basic-navbar-nav" />

        <Navbar.Collapse id="basic-navbar-nav">
          <Navbar.Brand
            className="d-none d-sm-block"
            style={{
              visibility: isProfessionalDashboardActive ? 'visible' : 'hidden',
            }}
          >
            <OverlayTrigger
              placement="bottom"
              overlay={<Tooltip id="navbar-brand-tooltip">{VocabTopBar.active_patient[lan]}</Tooltip>}
            >
              <div className="fixed-width patient-name">
                {activePatientFullName || '—'}
              </div>
            </OverlayTrigger>
          </Navbar.Brand>

          <Form
            className="mx-auto w-50"
            onSubmit={handleSearch}
            style={{
              visibility: isProfessionalDashboardActive ? 'visible' : 'hidden',
              pointerEvents: isProfessionalDashboardActive ? 'auto' : 'none',
            }}
          >
            <InputGroup>
              <InputGroup.Text className={shake ? 'shake' : ''} id="basic-addon1">
                @
              </InputGroup.Text>
              <Form.Control
                className={shake ? 'shake' : ''}
                placeholder="Username"
                aria-label="Username"
                aria-describedby="basic-addon1"
                value={searchUser}
                onChange={(e) => setSearchUser(e.target.value)}
              />
              <OverlayTrigger
                placement="bottom"
                overlay={<Tooltip id="navbar-brand-tooltip">{VocabTopBar.search_toolip[lan]}</Tooltip>}
              >
                <Button className={shake ? 'shake' : ''} variant="outline-light" type="submit">
                  {VocabTopBar.search[lan]}
                </Button>
              </OverlayTrigger>
              <OverlayTrigger
                placement="bottom"
                overlay={<Tooltip id="navbar-brand-tooltip">{VocabTopBar.clear_patient[lan]}</Tooltip>}
              >
                <Button variant="outline-light" onClick={handleClear}>
                  <img src={clean_icon} alt={'clean_icon'} width="20" height="20" />
                </Button>
              </OverlayTrigger>
            </InputGroup>
          </Form>

          <Nav className="ml-auto">
            <Navbar.Brand>
              {getGreetingMessage()} {user_fullname}
            </Navbar.Brand>

            <OverlayTrigger
              placement="bottom"
              overlay={<Tooltip id="notif-tooltip">{VocabTopBar.notifications[lan]}</Tooltip>}
            >
              <Nav.Link onClick={handleNotificationsClick} style={{ position: 'relative' }}>
                <BsBell size={20} />
                {unreadCount > 0 && (
                  <Badge
                    bg="danger"
                    pill
                    style={{
                      position: 'absolute',
                      top: '0',
                      right: '0',
                      fontSize: '0.6rem',
                      transform: 'translate(50%, -50%)',
                    }}
                  >
                    {unreadCount}
                  </Badge>
                )}
              </Nav.Link>
            </OverlayTrigger>

            <OverlayTrigger
              placement="bottom"
              overlay={<Tooltip id="settings-tooltip">{VocabTopBar.more[lan]}</Tooltip>}
            >
              <NavDropdown title={<BsGear size={20} />} id="navbarScrollingDropdown" align="end">
                {isProfessional && (
                  <>
                    <NavDropdown.Item onClick={handleProfileChange}>
                      {isProfessionalDashboardActive ? VocabTopBar.profile[lan] : VocabTopBar.pro_suite[lan]}
                    </NavDropdown.Item>
                    <NavDropdown.Divider />
                    <NavDropdown.Item onClick={() => setShowQR(true)}>
                      {VocabTopBar.get_my_qr[lan]}
                    </NavDropdown.Item>
                    <NavDropdown.Divider />
                  </>
                )}
                <NavDropdown.Item onClick={handleLogout}>
                  {VocabTopBar.logout[lan]}
                </NavDropdown.Item>
              </NavDropdown>
            </OverlayTrigger>
          </Nav>
        </Navbar.Collapse>
      </Navbar>
    </>
  );
};

export default TopBar;